import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import {
  Search, FolderOpen, Rows3, PieChart, Settings as SettingsIcon, Plus, WifiOff,
  RefreshCw, LogOut, Stethoscope, Lock, Delete, ShieldCheck, Users, ChevronDown, Check, CreditCard, CalendarDays,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { ClinicMark } from "@/components/Brand";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const SEARCH_ITEM = { to: "/app", label: "Search", icon: Search, testid: "nav-search", mobile: true };
const OVERVIEW_ITEM = { to: "/app/overview", label: "Overview", icon: PieChart, testid: "nav-overview", mobile: true, ownerOnly: true };
const CALENDAR_ITEM = { to: "/app/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar", mobile: true, badge: "today" };
const DIRECTORY_ITEM = { to: "/app/directory", label: "Directory", icon: FolderOpen, testid: "nav-directory", mobile: true };
const RECORDS_ITEM = { to: "/app/records", label: "Records", icon: Rows3, testid: "nav-records", mobile: true };
const CONSULTANTS_ITEM = { to: "/app/consultants", label: "Consultants", icon: Stethoscope, testid: "nav-consultants" };
const SETTINGS_ITEM = { to: "/app/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings", mobile: true };
const BILLING_ITEM = { to: "/app/billing", label: "Billing", icon: CreditCard, testid: "nav-billing", mobile: false };
const ADMIN_ITEM = { to: "/app/admin", label: "App Analytics", icon: ShieldCheck, testid: "nav-admin", adminOnly: true, end: true };
const ADMIN_USERS_ITEM = { to: "/app/admin/users", label: "User Management", icon: Users, testid: "nav-admin-users", adminOnly: true };

const NAV_GROUPS = [
  { title: null, items: [SEARCH_ITEM] },
  { title: "Clinic", items: [OVERVIEW_ITEM, CALENDAR_ITEM, DIRECTORY_ITEM, RECORDS_ITEM, CONSULTANTS_ITEM] },
  { title: "System", items: [BILLING_ITEM, SETTINGS_ITEM, ADMIN_ITEM, ADMIN_USERS_ITEM] },
];
const MOBILE_ITEMS = [SEARCH_ITEM, CALENDAR_ITEM, DIRECTORY_ITEM, RECORDS_ITEM, OVERVIEW_ITEM, SETTINGS_ITEM];

const visible = (items, isOwner, isSuperAdmin) =>
  items.filter((n) => (n.adminOnly ? isSuperAdmin : n.ownerOnly ? isOwner : true));

export const SyncPill = () => {
  const { online, syncing, pending } = useApp();
  if (!online)
    return (
      <span data-testid="offline-pill" className="dm-badge shrink-0 whitespace-nowrap border-amber-200 bg-amber-50 text-amber-700">
        <WifiOff size={12} /> Offline
      </span>
    );
  if (syncing || pending)
    return (
      <span data-testid="syncing-pill" className="dm-badge dm-badge-accent shrink-0 whitespace-nowrap">
        <RefreshCw size={12} className="animate-spin" /> Syncing{pending ? ` ${pending}` : ""}
      </span>
    );
  return null;
};

export const TrialPill = () => {
  const { subscription } = useApp();
  if (!subscription || subscription.status === "active") return null;
  if (subscription.status === "trialing") {
    if (subscription.days_left > 7) return null;
    return (
      <Link to="/app/billing" data-testid="trial-pill" className="dm-badge shrink-0 whitespace-nowrap border-amber-200 bg-amber-50 text-amber-700">
        {subscription.days_left}d left
      </Link>
    );
  }
  if (subscription.status === "past_due" || subscription.status === "expired") {
    return (
      <Link to="/app/billing" data-testid="trial-pill" className="dm-badge shrink-0 whitespace-nowrap border-red-200 bg-red-50 text-red-700">
        {subscription.status === "past_due" ? "Payment due" : "Ended"}
      </Link>
    );
  }
  return null;
};

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "✕", "0", "⌫"];

const initials = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "U";

function ProfileMenu({ full = false, onRequestDoctorMode }) {
  const { user, viewMode, isOwner, setMode, logout } = useApp();
  const nav = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="profile-menu-trigger"
        className={`flex items-center gap-2 rounded-lg py-1.5 pl-1 pr-2 transition-colors hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-white ${full ? "w-full" : ""}`}
        style={{ "--tw-ring-color": "var(--dm-accent)" }}>
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-[11px] font-bold text-white" style={{ backgroundColor: "var(--dm-accent)" }}>
          {initials(user?.name)}
        </span>
        <span className={`min-w-0 flex-1 truncate text-left text-sm font-semibold text-slate-800 ${full ? "" : "hidden max-w-[9rem] sm:block"}`}>{user?.name}</span>
        <ChevronDown size={14} className={`text-slate-400 ${full ? "" : "hidden sm:block"}`} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align={full ? "start" : "end"} side={full ? "top" : "bottom"} sideOffset={8}
        className="w-56 border-slate-200 bg-white text-slate-800 shadow-lg">
        <DropdownMenuLabel className="font-normal">
          <p className="truncate text-sm font-semibold text-slate-800">{user?.name}</p>
          <p className="truncate text-xs text-slate-500">{user?.email}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-slate-100" />
        <div className="px-2 py-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-500">Role</span>
            <span className="font-semibold text-slate-700">{isOwner ? "Owner" : "Assistant"}</span>
          </div>
        </div>
        {isOwner && (
          <>
            <DropdownMenuSeparator className="bg-slate-100" />
            <div className="px-2 py-1.5">
              <p className="mb-1.5 text-xs text-slate-500">
                Viewing as <span className="font-semibold text-slate-700">{viewMode === "doctor" ? "Doctor" : "Assistant"}</span>
              </p>
              {viewMode === "doctor" ? (
                <DropdownMenuItem data-testid="switch-assistant-mode" onSelect={(e) => { e.preventDefault(); setMode("assistant"); toast.success("Locked to Assistant Mode"); }}
                  className="cursor-pointer justify-center rounded-md border border-slate-200 text-center">
                  <Lock size={13} /> Switch to Assistant Mode
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem data-testid="switch-doctor-mode" onSelect={(e) => { e.preventDefault(); onRequestDoctorMode?.(); }}
                  className="cursor-pointer justify-center rounded-md border border-slate-200 text-center">
                  <Lock size={13} /> Switch to Doctor Mode
                </DropdownMenuItem>
              )}
            </div>
          </>
        )}
        <DropdownMenuSeparator className="bg-slate-100" />
        <DropdownMenuItem data-testid="logout-btn" onClick={() => { logout(); nav("/"); }}
          className="cursor-pointer text-red-700 focus:bg-red-50 focus:text-red-800">
          <LogOut size={15} /> Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout() {
  const { clinic, data, viewMode, setMode, isOwner, isSuperAdmin, pullAll } = useApp();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const showFab = ["/app", "/app/directory", "/app/records"].includes(pathname) || pathname.startsWith("/app/patients/");
  const [taps, setTaps] = useState(0);
  const [pinOpen, setPinOpen] = useState(false);
  const [pin, setPin] = useState("");

  const today = new Date().toLocaleDateString("en-CA");
  const weekAgo = new Date(Date.now() - 6 * 86400000).toLocaleDateString("en-CA");
  const scheduledToday = data.patients.filter((p) => p.next_appointment === today).length;
  const loggedToday = data.visits.filter((v) => v.date === today).length;
  const mobileItems = visible(MOBILE_ITEMS, isOwner, isSuperAdmin);
  const newThisWeek = data.patients.filter((p) => (p.created_at || "").slice(0, 10) >= weekAgo).length;
  const badges = { today: scheduledToday };

  const onLogoTap = () => {
    if (viewMode === "doctor") {
      setTaps(0);
      setMode("assistant");
      return toast.success("Locked to Assistant Mode");
    }
    const t = taps + 1;
    setTaps(t);
    if (t >= 3) {
      setTaps(0);
      if (!isOwner) return toast.error("Assistant accounts cannot switch to Doctor Mode");
      setPinOpen(true);
    }
    setTimeout(() => setTaps(0), 1400);
  };

  const press = (k) => {
    if (k === "✕") return closePin();
    if (k === "⌫") return setPin(pin.slice(0, -1));
    const next = (pin + k).slice(0, 4);
    setPin(next);
    if (next.length === 4) submitPin(next);
  };

  const submitPin = (value) => {
    if (value === String(clinic?.quick_pin || "1234")) {
      setMode("doctor");
      closePin();
      toast.success("Doctor Mode unlocked");
    } else {
      toast.error("Wrong PIN");
      setPin("");
    }
  };

  const closePin = () => {
    setPinOpen(false);
    setPin("");
  };

  return (
    <div className="min-h-screen min-h-dvh pb-24 md:pb-0 md:pl-[248px]">
      <aside className="fixed left-0 top-0 z-30 hidden h-full w-[248px] p-2.5 md:block">
        <div className="flex h-full flex-col rounded-[20px] border border-slate-200/70 bg-white" style={{ boxShadow: "0 1px 2px rgba(15,23,42,.04)" }}>
          <button data-testid="clinic-logo-tap" onClick={onLogoTap} className="flex items-center gap-2.5 px-3.5 pb-3 pt-4 text-left">
            <ClinicMark logo={clinic?.logo_url} size={32} />
            <span className="min-w-0">
              <span className="block truncate font-head text-[13px] font-bold text-slate-900">{clinic?.name || "DentMemo"}</span>
              <span className="block truncate text-[11.5px] text-slate-500">{clinic?.dentist_name || "Second brain for dentists."}</span>
            </span>
          </button>

          <div className="px-2.5">
            <NavLink to="/app/log-visit" data-testid="nav-visit"
              className={({ isActive }) =>
                `dm-btn-primary flex !w-full !justify-start !px-3.5 !py-2 text-[13px] ${isActive ? "!opacity-70" : ""}`
              }>
              {({ isActive }) => (
                <>{isActive ? <Check size={16} /> : <Plus size={16} />} {isActive ? "Logging visit…" : "Log visit"}</>
              )}
            </NavLink>
          </div>

          <nav className="mt-1 flex-1 space-y-3.5 overflow-y-auto px-2.5 py-3">
            {NAV_GROUPS.map((group, gi) => {
              const items = visible(group.items, isOwner, isSuperAdmin);
              if (items.length === 0) return null;
              return (
                <div key={gi}>
                  {group.title && <p className="px-3 pb-1 text-[11px] font-medium text-slate-400">{group.title}</p>}
                  <div className="space-y-0.5">
                    {items.map((n) => {
                      const count = n.badge ? badges[n.badge] : 0;
                      return (
                        <NavLink key={n.to} to={n.to} end={n.to === "/app" || n.end} data-testid={n.testid}
                          className={({ isActive }) => `dm-nav-item group ${isActive ? "dm-nav-item-on" : ""}`}>
                          <n.icon size={16} className="shrink-0" />
                          <span className="flex-1 truncate">{n.label}</span>
                          {count > 0 && (
                            <span data-testid={`${n.testid}-badge`} aria-label={`${count} booked today`}
                              className="dm-nav-badge">{count > 99 ? "99+" : count}</span>
                          )}
                        </NavLink>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </nav>

          <div className="mx-2.5 mb-2.5 grid grid-cols-3 gap-0.5 rounded-xl bg-slate-50 p-1.5" data-testid="sidebar-today">
            {[["Visits", loggedToday, "today"], ["Booked", scheduledToday, "today", "/app/calendar"], ["New", newThisWeek, "this week"]].map(([l, v, sub, to]) => {
              const inner = (
                <>
                  <span className="dm-figure block text-[15px] font-bold leading-tight text-slate-900">{v}</span>
                  <span className="block text-[10.5px] leading-tight text-slate-500">{l}<br />{sub}</span>
                </>
              );
              return to
                ? <Link key={l} to={to} data-testid="sidebar-scheduled" className="rounded-xl px-2 py-1.5 transition-colors hover:bg-white">{inner}</Link>
                : <div key={l} className="px-2 py-1.5">{inner}</div>;
            })}
          </div>

          <div className="border-t border-slate-100 p-2">
            <ProfileMenu full onRequestDoctorMode={() => setPinOpen(true)} />
          </div>
        </div>
      </aside>

      <header className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-slate-200/60 bg-[#eef1f5]/90 px-4 py-2 backdrop-blur-xl md:px-7">
        <button onClick={onLogoTap} data-testid="clinic-logo-tap-mobile" className="flex min-w-0 items-center gap-2.5 md:hidden">
          <ClinicMark logo={clinic?.logo_url} size={32} />
          <span className="truncate font-head text-sm font-bold text-slate-900">{clinic?.name || "DentMemo"}</span>
        </button>
        <p className="hidden items-center gap-2 text-xs text-slate-500 md:flex">
          <Lock size={13} className="text-slate-500" />
          {viewMode === "doctor" ? "Doctor Access — tap the logo to lock" : "Financial details are visible only in PIN-protected doctor mode."}
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <TrialPill />
          <SyncPill />
          <button data-testid="manual-sync-btn" onClick={() => pullAll()} className="shrink-0 rounded-full border border-slate-200 p-2 text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-800">
            <RefreshCw size={14} />
          </button>
          <div className="shrink-0 md:hidden">
            <ProfileMenu onRequestDoctorMode={() => setPinOpen(true)} />
          </div>
        </div>
      </header>

      <main className="fade-up mx-auto w-full max-w-[1240px] px-4 py-5 md:px-7 md:py-6"><Outlet /></main>

      <nav className="fixed bottom-0 left-0 right-0 z-20 grid border-t border-slate-200 bg-white/95 backdrop-blur-xl md:hidden"
        style={{ gridTemplateColumns: `repeat(${mobileItems.length}, minmax(0, 1fr))` }}>
        {mobileItems.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === "/app"} data-testid={`${n.testid}-mobile`}
            className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[10px] font-semibold transition-colors ${isActive ? "dm-accent-text" : "text-slate-500"}`}>
            <n.icon size={19} /> {n.label}
          </NavLink>
        ))}
      </nav>

      {showFab && (
        <button data-testid="fab-log-visit" onClick={() => nav("/app/log-visit")}
          className="dm-accent-bg fixed bottom-20 right-4 z-30 grid h-14 w-14 place-items-center rounded-full text-white transition-transform active:scale-95 md:hidden"
          style={{ boxShadow: "0 10px 30px rgba(0,0,0,.5)" }}>
          <Plus size={26} />
        </button>
      )}

      {pinOpen && (
        <div className="fixed inset-0 z-40 grid place-items-center bg-slate-950/70 p-4 backdrop-blur-sm">
          <div data-testid="pin-dialog" className="dm-panel w-full max-w-[300px] bg-white p-6 text-center" style={{ boxShadow: "0 8px 32px rgba(0,0,0,.55)" }}>
            <h3 className="font-head text-xl font-bold text-slate-900">Doctor Access</h3>
            <p className="mt-1 text-sm text-slate-600">Enter PIN to unlock</p>
            <div className="my-5 flex justify-center gap-3" data-testid="pin-dots">
              {[0, 1, 2, 3].map((i) => (
                <span key={i} className={`h-3 w-3 rounded-full transition-colors ${i < pin.length ? "dm-accent-bg" : "bg-slate-300"}`} />
              ))}
            </div>
            <input data-testid="pin-input" className="sr-only" value={pin} onChange={(e) => {
              const v = e.target.value.replace(/\D/g, "").slice(0, 4);
              setPin(v);
              if (v.length === 4) submitPin(v);
            }} />
            <div className="grid grid-cols-3 gap-2">
              {KEYS.map((k) => (
                <button key={k} data-testid={`pin-key-${k === "✕" ? "cancel" : k === "⌫" ? "back" : k}`} onClick={() => press(k)}
                  className="dm-btn-ghost !rounded-xl !py-3 font-mono text-lg">
                  {k === "⌫" ? <Delete size={17} /> : k}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
